import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useRestoreLedger } from '@/api/hooks/useLedgers';
import { toast } from 'sonner';
import type { Ledger } from '@/api/types';

interface RestoreConfirmDialogProps {
    ledger: Ledger;
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Simple confirmation dialog for restoring an archived ledger.
 */
export function RestoreConfirmDialog({ ledger, isOpen, onClose }: RestoreConfirmDialogProps) {
    const restoreMutation = useRestoreLedger();
    const typeLabel = ledger.type === 'asset' ? 'asset' : 'debt';

    const handleRestore = async () => {
        try {
            await restoreMutation.mutateAsync(ledger.id);
            toast.success(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} restored`);
            onClose();
        } catch {
            toast.error(`Failed to restore ${typeLabel}`);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title={`Restore ${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)}`}
            size="sm"
        >
            <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    This {typeLabel} is archived. Restore{' '}
                    <strong className="font-semibold text-gray-900 dark:text-gray-100">
                        {ledger.label}
                    </strong>{' '}
                    to continue tracking it?
                </p>

                <div className="flex justify-end gap-3 pt-2">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={restoreMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleRestore}
                        isLoading={restoreMutation.isPending}
                    >
                        Restore
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
